import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Users, Award, Calendar, ChevronDown } from "lucide-react";

const stats = [
  { value: "1967", label: "Үүсгэн байгуулагдсан" },
  { value: "200+", label: "Гишүүн эмч нар" },
  { value: "50+", label: "Арга хэмжээ" },
];

export default function CoverNew() {
  return (
    <section
      style={{
        minHeight: "100vh",
        background: "linear-gradient(150deg, #001845 0%, #0047AB 45%, #0076A3 75%, #00B4D8 100%)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {/* Background decoration */}
      <div style={{
        position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none",
      }}>
        {/* Large blurred circle top-right */}
        <div style={{
          position: "absolute", top: "-15%", right: "-10%",
          width: 600, height: 600,
          background: "radial-gradient(circle, rgba(0,180,216,0.25) 0%, transparent 70%)",
          borderRadius: "50%",
        }} />
        {/* Small accent circle bottom-left */}
        <div style={{
          position: "absolute", bottom: "10%", left: "-5%",
          width: 400, height: 400,
          background: "radial-gradient(circle, rgba(244,164,10,0.15) 0%, transparent 70%)",
          borderRadius: "50%",
        }} />
        {/* Grid pattern */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }} />
        {/* Floating orbs */}
        <div className="animate-float" style={{
          position: "absolute", top: "20%", right: "15%",
          width: 80, height: 80,
          background: "rgba(255,255,255,0.06)",
          borderRadius: "50%", border: "1px solid rgba(255,255,255,0.1)",
          animationDelay: "0s",
        }} />
        <div className="animate-float" style={{
          position: "absolute", top: "55%", right: "8%",
          width: 48, height: 48,
          background: "rgba(0,180,216,0.15)",
          borderRadius: "50%", border: "1px solid rgba(0,180,216,0.3)",
          animationDelay: "2s",
        }} />
        <div className="animate-float" style={{
          position: "absolute", bottom: "25%", left: "12%",
          width: 64, height: 64,
          background: "rgba(244,164,10,0.12)",
          borderRadius: "50%", border: "1px solid rgba(244,164,10,0.25)",
          animationDelay: "4s",
        }} />
      </div>

      {/* Main content */}
      <div style={{
        maxWidth: 1200, margin: "0 auto", padding: "120px 24px 80px",
        display: "grid", gridTemplateColumns: "1fr 1fr",
        gap: 64, alignItems: "center",
        position: "relative", zIndex: 1,
      }}
        className="hero-grid"
      >
        {/* Left — Text */}
        <div className="animate-fade-up" style={{ animationDelay: "0.1s" }}>
          {/* Badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 100, padding: "6px 16px",
            marginBottom: 28,
            backdropFilter: "blur(8px)",
          }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#F4A40A" }} className="animate-pulse-slow" />
            <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 13, fontWeight: 500, letterSpacing: "0.04em" }}>
              1967 онд үүсгэн байгуулагдсан
            </span>
          </div>

          {/* Heading */}
          <h1 style={{
            fontSize: "clamp(2rem, 4vw, 3.2rem)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.15,
            marginBottom: 20,
            letterSpacing: "-0.02em",
            fontFamily: "'Outfit', 'Inter', sans-serif",
          }}>
            Монголын Чих Хамар Хоолойн{" "}
            <span style={{
              background: "linear-gradient(135deg, #48CAE4 0%, #F4A40A 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
              Эмч Нарын Нийгэмлэг
            </span>
          </h1>

          <p style={{
            color: "rgba(255,255,255,0.72)",
            fontSize: 17,
            lineHeight: 1.75,
            marginBottom: 40,
            maxWidth: 480,
          }}>
            Чих хамар хоолойн эмч нар болон мэргэжилтнүүдийг нэгтгэсэн, мэдлэг мэргэжлийг дээшлүүлэх зорилго бүхий ашгийн бус, төрийн бус байгууллага.
          </p>

          {/* CTA Buttons */}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Link href="/about">
              <button style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "#ffffff",
                color: "#0047AB",
                border: "none",
                borderRadius: 12,
                padding: "14px 28px",
                fontSize: 15, fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                transition: "all 0.25s ease",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.25)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.2)"; }}
              >
                Танилцуулга <ArrowRight size={16} />
              </button>
            </Link>
            <Link href="/contact">
              <button style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "rgba(255,255,255,0.1)",
                color: "#ffffff",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 12,
                padding: "14px 28px",
                fontSize: 15, fontWeight: 600,
                cursor: "pointer",
                backdropFilter: "blur(8px)",
                transition: "all 0.25s ease",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.18)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
              >
                Холбоо барих
              </button>
            </Link>
          </div>

          {/* Stats row */}
          <div style={{
            display: "flex", gap: 32, marginTop: 56,
            paddingTop: 40,
            borderTop: "1px solid rgba(255,255,255,0.12)",
          }}>
            {stats.map((s, i) => (
              <div key={i} className="animate-fade-up" style={{ animationDelay: `${0.3 + i * 0.1}s` }}>
                <div style={{ fontSize: "clamp(1.4rem, 2.5vw, 2rem)", fontWeight: 800, color: "#ffffff", lineHeight: 1, fontFamily: "'Outfit', sans-serif" }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Image card */}
        <div className="animate-fade-up" style={{ animationDelay: "0.25s", position: "relative" }}>
          {/* Glow behind image */}
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%", height: "90%",
            background: "radial-gradient(circle, rgba(0,180,216,0.3) 0%, transparent 70%)",
            filter: "blur(40px)", borderRadius: "50%",
          }} />
          <div className="animate-float" style={{
            position: "relative", zIndex: 1,
            borderRadius: 24,
            overflow: "hidden",
            boxShadow: "0 40px 80px rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}>
            <Image
              src="/coverPhoto.jpg"
              alt="Нийгэмлэгийн гишүүд"
              width={560}
              height={420}
              style={{ width: "100%", height: "auto", display: "block", objectFit: "cover" }}
              priority
            />
            {/* Image overlay gradient */}
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0, height: "40%",
              background: "linear-gradient(to top, rgba(0,16,60,0.7) 0%, transparent 100%)",
            }} />
            {/* Floating badge on image */}
            <div className="glass" style={{
              position: "absolute", bottom: 20, left: 20,
              borderRadius: 12, padding: "10px 16px",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{
                width: 36, height: 36,
                background: "linear-gradient(135deg, #0047AB, #00B4D8)",
                borderRadius: 8,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Award size={18} color="white" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0A0F1E" }}>Мэргэжлийн нийгэмлэг</div>
                <div style={{ fontSize: 11, color: "#64748B" }}>Олон улсын гишүүнчлэлтэй</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{
        position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        color: "rgba(255,255,255,0.4)", fontSize: 12, zIndex: 1,
        animation: "fadeIn 1s ease 1s both",
      }}>
        <span style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}>Доош</span>
        <ChevronDown size={18} style={{ animation: "float 2s ease-in-out infinite" }} />
      </div>

      {/* Mobile responsive */}
      {/* <style>{`
        @media (max-width: 768px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            padding-top: 100px !important;
            gap: 40px !important;
          }
          .hero-grid > div:last-child { display: none; }
        }
      `}</style> */}
    </section>
  );
}
